/**
 * Mystery Box Rarity System
 * Handles CSV parsing, rarity calculation, and interactive displays
 */

class MysteryBoxRarity {
  constructor(csvData, options = {}) {
    this.rawData = csvData;
    this.options = {
      showOdds: options.showOdds !== false,
      showCounts: options.showCounts !== false,
      ...options
    };
    this.products = [];
    this.rarityTiers = {
      legendary: [],
      rare: [],
      common: []
    };
    this.init();
  }

  /**
   * Initialize the rarity system
   */
  init() {
    this.parseCSV();
    this.calculateRarities();
    this.calculateOdds();
  }

  /**
   * Parse CSV data into product objects
   */
  parseCSV() {
    const lines = this.rawData.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = this.parseCSVLine(line);
      const product = {};

      headers.forEach((header, index) => {
        product[header] = values[index] ? values[index].trim() : '';
      });

      // Parse numeric values
      product.price = parseFloat(product.price) || 0;
      product.quantity = parseInt(product.quantity) || 1;

      this.products.push(product);
    }
  }

  /**
   * Parse CSV line handling quoted values and escaped quotes
   * Correctly handles CSV escaping: "" becomes "
   */
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote ("") - add single quote to value
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state (start/end of quoted field)
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator outside quotes
        values.push(current);
        current = '';
      } else {
        // Regular character
        current += char;
      }
    }
    values.push(current);

    return values;
  }

  /**
   * Calculate rarity tier for each product
   * Based on grade (A/B/C) from product metafield
   * Grade A → legendary, Grade B → rare, Grade C → common
   */
  calculateRarities() {
    // Map grades to tiers
    this.products.forEach(product => {
      const grade = (product.grade || '').toUpperCase().trim();

      if (grade === 'A') {
        product.rarity = 'legendary';
        this.rarityTiers.legendary.push(product);
      } else if (grade === 'B') {
        product.rarity = 'rare';
        this.rarityTiers.rare.push(product);
      } else if (grade === 'C') {
        product.rarity = 'common';
        this.rarityTiers.common.push(product);
      } else {
        // If no grade or invalid grade, fallback to price-based assignment
        // This ensures backwards compatibility for mystery boxes without grades
        console.warn(`Product "${product.product_name}" has no valid grade (${grade}), using price-based fallback`);
        product.needsPriceBasedRarity = true;
      }
    });

    // Handle products without grades using price-based fallback
    const productsWithoutGrades = this.products.filter(p => p.needsPriceBasedRarity);
    if (productsWithoutGrades.length > 0) {
      console.log(`Applying price-based rarity to ${productsWithoutGrades.length} products without grades`);
      this.applyPriceBasedRarity(productsWithoutGrades);
    }
  }

  /**
   * Fallback: Price-based rarity assignment for products without grades
   * Only used for backwards compatibility with mystery boxes that don't have grades set
   */
  applyPriceBasedRarity(products) {
    const sortedByPrice = [...products].sort((a, b) => b.price - a.price);
    const totalProducts = sortedByPrice.length;

    const legendaryCount = Math.ceil(totalProducts * 0.15);
    const rareCount = Math.ceil(totalProducts * 0.30);

    let index = 0;

    for (let i = 0; i < legendaryCount && index < totalProducts; i++, index++) {
      sortedByPrice[index].rarity = 'legendary';
      this.rarityTiers.legendary.push(sortedByPrice[index]);
    }

    for (let i = 0; i < rareCount && index < totalProducts; i++, index++) {
      sortedByPrice[index].rarity = 'rare';
      this.rarityTiers.rare.push(sortedByPrice[index]);
    }

    while (index < totalProducts) {
      sortedByPrice[index].rarity = 'common';
      this.rarityTiers.common.push(sortedByPrice[index]);
      index++;
    }
  }

  /**
   * Calculate odds for each rarity tier
   */
  calculateOdds() {
    const totalQuantity = this.products.reduce((sum, p) => sum + p.quantity, 0);

    Object.keys(this.rarityTiers).forEach(rarity => {
      const tierQuantity = this.rarityTiers[rarity].reduce((sum, p) => sum + p.quantity, 0);
      const percentage = totalQuantity > 0 ? (tierQuantity / totalQuantity * 100).toFixed(1) : 0;

      this.rarityTiers[rarity].odds = parseFloat(percentage);
      this.rarityTiers[rarity].count = this.rarityTiers[rarity].length;
      this.rarityTiers[rarity].totalQuantity = tierQuantity;
    });
  }

  /**
   * Get rarity tier data
   */
  getTier(rarity) {
    return this.rarityTiers[rarity] || [];
  }

  /**
   * Get all tiers
   */
  getAllTiers() {
    return this.rarityTiers;
  }

  /**
   * Get formatted rarity name (supports custom tier names from options)
   */
  getRarityLabel(rarity) {
    // Use custom tier names if provided in options
    if (this.options.tierNames && this.options.tierNames[rarity]) {
      const emojis = {
        legendary: '⭐',
        rare: '🎯',
        common: '📦'
      };
      return `${emojis[rarity] || ''} ${this.options.tierNames[rarity]}`;
    }

    // Default labels (updated to Grade A/B/C)
    const labels = {
      legendary: '⭐ Grade A',
      rare: '🎯 Grade B',
      common: '📦 Grade C'
    };
    return labels[rarity] || rarity;
  }

  /**
   * Render products as HTML list with images
   */
  renderProductList(products) {
    if (!products || products.length === 0) {
      return '<p style="text-align: center; color: #9CA3AF; padding: 20px;">No items in this tier</p>';
    }

    // Show consistent preview count: 5 items for all tiers (or 6 for 50+)
    let previewCount = 5;
    if (products.length > 50) previewCount = 6;

    const displayProducts = products.slice(0, previewCount);
    const remaining = products.length - previewCount;

    return `
      <ul class="rarity-product-list">
        ${displayProducts.map((product, index) => {
          // Get product details from productDetails if available
          let imageUrl = '';
          const currency = window.MYSTERY_BOX_CURRENCY || 'CAD';
          let priceFormatted = '$' + product.price.toFixed(0) + ' ' + currency; // fallback

          if (this.productDetails && Array.isArray(this.productDetails)) {
            const productDetail = this.productDetails.find(p => p.product_name === product.product_name);
            if (productDetail) {
              imageUrl = productDetail.image || '';
              // Use compareAtPrice if available (MSRP), otherwise use regular price
              priceFormatted = productDetail.compareAtPriceFormatted || productDetail.priceFormatted || priceFormatted;
            }
          }

          return `
            <li class="rarity-product-item" data-product-name="${product.product_name.replace(/"/g, '&quot;')}">
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.product_name}" class="rarity-product-image" loading="lazy">` : ''}
              <div class="rarity-product-info">
                <span class="rarity-product-name">${product.product_name}</span>
                <span class="rarity-product-price">${priceFormatted}</span>
              </div>
            </li>
          `;
        }).join('')}
        ${remaining > 0 ? `
          <li class="rarity-view-all-trigger" data-action="view-all">
            <span style="display: inline-flex; align-items: center; gap: 8px; justify-content: center;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="14" width="7" height="7" rx="1"></rect>
                <rect x="3" y="14" width="7" height="7" rx="1"></rect>
              </svg>
              View all ${products.length} items
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </span>
          </li>
        ` : ''}
    `;
  }
}

/**
 * Vertical Scrolling Sections Controller (Option C)
 */
class VerticalSections {
  constructor(container, mysteryBox) {
    this.container = container;
    this.mysteryBox = mysteryBox;
    this.render();
  }

  render() {
    const tiers = ['legendary', 'rare', 'common'];
    const allTiers = this.mysteryBox.getAllTiers();

    const sectionsHTML = `
      <div class="rarity-sections">
        ${tiers.map(tier => {
          const tierData = allTiers[tier];
          return `
            <section class="rarity-section rarity-section--${tier}" data-tier="${tier}">
              <div class="rarity-section__header">
                <div class="rarity-badge rarity-badge--${tier}">
                  ${this.mysteryBox.getRarityLabel(tier)}
                  ${this.mysteryBox.options.showCounts ? ` <span class="rarity-badge-count">(${tierData.count} items)</span>` : ''}
                </div>
                ${this.mysteryBox.options.showOdds ? `
                  <div class="rarity-stats">
                    <div class="rarity-stat">
                      <span class="rarity-stat__icon">📊</span>
                      <span>${tierData.odds}% chance</span>
                    </div>
                  </div>
                ` : ''}
              </div>
              ${this.mysteryBox.renderProductList(tierData)}
            </section>
          `;
        }).join('')}
      </div>
    `;

    this.container.innerHTML = sectionsHTML;
  }
}

/**
 * Filterable Grid Controller (Option D)
 */
/**
 * ============================================
 * Mystery Box Modal Controller
 * (Consolidated from mystery-box-modal.js)
 * ============================================
 */

// Authentication badge configuration
// Logo URLs are loaded from window.MYSTERY_BOX_AUTH_LOGOS (set by section)
const AUTH_CONFIG = {
  frameworth: {
    get logo() { return window.MYSTERY_BOX_AUTH_LOGOS?.frameworth || null; },
    color: '#D4A843',
    logoHeight: '13px',
    sizing: {
      targetWidth: 64,
      minHeight: 12,
      maxHeight: 20
    }
  },
  'aj sports': {
    get logo() { return window.MYSTERY_BOX_AUTH_LOGOS?.['aj sports'] || null; },
    color: '#ea580c',
    logoHeight: '13px',
    sizing: {
      targetWidth: 64,
      minHeight: 12,
      maxHeight: 20
    }
  },
  fanatics: {
    get logo() { return window.MYSTERY_BOX_AUTH_LOGOS?.fanatics || null; },
    color: '#059669',
    logoHeight: '11px',
    sizing: {
      targetWidth: 68,
      minHeight: 11,
      maxHeight: 18
    }
  },
  'upper deck': {
    get logo() { return window.MYSTERY_BOX_AUTH_LOGOS?.['upper deck'] || null; },
    color: '#005396',
    logoHeight: '15px',
    sizing: {
      targetWidth: 62,
      minHeight: 15,
      maxHeight: 30
    }
  },
  other: {
    logo: null,
    color: '#6b7280',
    logoHeight: '13px',
    sizing: {
      targetWidth: 60,
      minHeight: 12,
      maxHeight: 20
    }
  }
};

const DEFAULT_LOGO_SIZING = {
  targetWidth: 60,
  minHeight: 12,
  maxHeight: 22
};

/**
 * Normalize authentication provider name
 */
function normalizeAuthName(name) {
  if (!name) return 'other';
  const lower = name.toLowerCase().trim();
  if (lower.includes('frameworth')) return 'frameworth';
  if (lower.includes('aj sport')) return 'aj sports';
  if (lower.includes('fanatics')) return 'fanatics';
  if (lower.includes('upper deck') || lower.includes('upperdeck') || /\buda\b/.test(lower)) return 'upper deck';
  return 'other';
}

/**
 * Generate authentication badge HTML
 */
function getAuthBadgeHTML(authenticatedBy) {
  if (!authenticatedBy) return '';

  const normalized = normalizeAuthName(authenticatedBy);
  const config = AUTH_CONFIG[normalized] || AUTH_CONFIG.other;
  const logoUrl = config.logo;
  const bgColor = config.color;
  const logoHeight = config.logoHeight;
  const logoHeightValue = typeof logoHeight === 'string'
    ? parseInt(logoHeight, 10)
    : Number.isFinite(logoHeight) ? Number(logoHeight) : NaN;
  const baseHeight = Number.isFinite(logoHeightValue) ? logoHeightValue : 13;

  if (logoUrl) {
    // Logo badge
    return `
      <div class="mb-auth-badge mb-auth-badge--logo" data-auth-provider="${normalized}">
        <img src="${logoUrl}" alt="${normalized}" class="mb-auth-badge__logo" data-provider="${normalized}" data-base-height="${baseHeight}" loading="lazy" />
        <span class="mb-auth-badge__text" style="color: ${bgColor};">COA</span>
      </div>
    `;
  } else {
    // Text-only fallback badge
    return `
      <div class="mb-auth-badge mb-auth-badge--text" style="background-color: ${bgColor};">
        <svg class="mb-auth-badge__icon" width="8" height="8" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span class="mb-auth-badge__text">${normalized.toUpperCase()} COA</span>
      </div>
    `;
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getAuthSizing(provider) {
  const config = AUTH_CONFIG[provider] || AUTH_CONFIG.other;
  const sizing = config && config.sizing ? config.sizing : {};
  return {
    targetWidth: sizing.targetWidth || DEFAULT_LOGO_SIZING.targetWidth,
    minHeight: sizing.minHeight || DEFAULT_LOGO_SIZING.minHeight,
    maxHeight: sizing.maxHeight || DEFAULT_LOGO_SIZING.maxHeight
  };
}

function applyAuthLogoSizing(container) {
  if (!container) return;

  const logos = container.querySelectorAll('.mb-auth-badge__logo');
  if (!logos.length) return;

  logos.forEach(img => {
    const provider = img.dataset.provider || (img.closest('[data-auth-provider]')?.dataset.authProvider) || 'other';
    const config = AUTH_CONFIG[provider] || AUTH_CONFIG.other;
    const sizing = getAuthSizing(provider);
    const baseHeightAttr = img.dataset.baseHeight;
    const baseHeightNumber = baseHeightAttr ? parseInt(baseHeightAttr, 10) : null;
    const baseHeight = Number.isFinite(baseHeightNumber) ? baseHeightNumber : (config.logoHeight ? parseInt(config.logoHeight, 10) || DEFAULT_LOGO_SIZING.minHeight : DEFAULT_LOGO_SIZING.minHeight);
    const minHeight = Math.max(baseHeight, sizing.minHeight || DEFAULT_LOGO_SIZING.minHeight);
    const maxHeight = Math.max(minHeight, sizing.maxHeight || DEFAULT_LOGO_SIZING.maxHeight);
    const targetWidth = sizing.targetWidth || DEFAULT_LOGO_SIZING.targetWidth;

    const resize = () => {
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      if (!naturalWidth || !naturalHeight) {
        img.style.height = `${minHeight}px`;
        img.style.width = 'auto';
        img.style.maxWidth = `${targetWidth}px`;
        return;
      }

      const ratio = naturalWidth / naturalHeight;
      if (!isFinite(ratio) || ratio <= 0) {
        img.style.height = `${minHeight}px`;
        img.style.width = 'auto';
        img.style.maxWidth = `${targetWidth}px`;
        return;
      }

      let desiredHeight = targetWidth / ratio;
      desiredHeight = clamp(desiredHeight, minHeight, maxHeight);

      // Boost nearly-square logos up to provide better presence
      if (ratio < 1.75 && desiredHeight < maxHeight) {
        desiredHeight = clamp(desiredHeight * 1.15, minHeight, maxHeight);
      }

      let finalWidth = Math.round(desiredHeight * ratio);
      if (finalWidth > targetWidth) {
        finalWidth = targetWidth;
        desiredHeight = clamp(finalWidth / ratio, minHeight, maxHeight);
      }

      img.style.height = `${Math.round(desiredHeight)}px`;
      img.style.width = `${Math.round(finalWidth)}px`;
      img.style.maxWidth = `${targetWidth}px`;
    };

    if (img.complete && img.naturalWidth) {
      resize();
    } else {
      img.addEventListener('load', resize, { once: true });
    }
  });
}

/**
 * Mystery Box Modal Controller Class
 */
class MysteryBoxModalController {
  constructor(container, modal, config) {
    // Prevent duplicate initialization on the same modal
    if (modal._mbModalController) {
      console.log('Modal controller already initialized, skipping duplicate');
      return modal._mbModalController;
    }

    this.container = container;
    this.modal = modal;
    this.config = config;

    // Modal elements
    this.modalTitle = modal.querySelector('[data-modal-title]');
    this.modalCount = modal.querySelector('[data-modal-count]');
    this.modalGrid = modal.querySelector('[data-modal-grid]');
    this.filterTabs = modal.querySelectorAll('[data-modal-filter]');
    this.filterScrollContainer = modal.querySelector('[data-modal-filters]');
    this.prevButton = modal.querySelector('[data-modal-filter-nav="prev"]');
    this.nextButton = modal.querySelector('[data-modal-filter-nav="next"]');
    this.closeBtn = modal.querySelector('[data-modal-close]');
    this.categoryTabs = modal.querySelectorAll('[data-modal-category]');

    // State
    this.currentFilter = 'all';
    this.currentCategory = 'jerseys'; // default category
    this.productsData = {
      jerseys: [],
      collectibles: []
    };
    this.isModalOpening = false;

    // Mark modal as initialized and store reference
    modal._mbModalController = this;

    this.init();
  }

  init() {
    // Load both jersey and collectible datasets FIRST
    this.loadCategoryData();

    // Update category tabs visibility based on available data
    this.updateCategoryTabs();

    // Attach event listeners
    this.attachEventListeners();

    // Initialize scroll arrows
    this.initScrollArrows();

    console.log('Modal controller initialized for modal:', this.modal.id);
  }

  /**
   * Load both jersey and collectible datasets from global window stores
   * These are populated by each section after tier calculation
   */
  loadCategoryData() {
    console.log('Loading category data from global stores...');

    // Detect if collectibles section exists on the current page.
    // Some mystery boxes (e.g. NHL) intentionally omit collectibles,
    // but global state from other pages could linger between Turbo visits.
    const collectiblesSectionPresent = !!document.querySelector('[data-mystery-box-modal][data-category="collectibles"]');

    // Load from global stores populated by sections
    if (window.MYSTERY_BOX_JERSEYS_DATA && window.MYSTERY_BOX_JERSEYS_DATA.length > 0) {
      this.productsData.jerseys = window.MYSTERY_BOX_JERSEYS_DATA;
      console.log('Loaded jerseys data from global store:', this.productsData.jerseys.length, 'products');

      // Log tier distribution
      const tierCounts = { legendary: 0, rare: 0, common: 0 };
      this.productsData.jerseys.forEach(p => {
        tierCounts[p.tier] = (tierCounts[p.tier] || 0) + 1;
      });
      console.log('Jerseys tier distribution:', tierCounts);
    } else {
      this.productsData.jerseys = [];
      console.warn('No jerseys data found in window.MYSTERY_BOX_JERSEYS_DATA');
    }

    // Load collectibles data
    if (window.MYSTERY_BOX_COLLECTIBLES_DATA && window.MYSTERY_BOX_COLLECTIBLES_DATA.length > 0) {
      this.productsData.collectibles = window.MYSTERY_BOX_COLLECTIBLES_DATA;
      console.log('Loaded collectibles data from global store:', this.productsData.collectibles.length, 'products');

      // Log tier distribution
      const tierCounts = { legendary: 0, rare: 0, common: 0 };
      this.productsData.collectibles.forEach(p => {
        tierCounts[p.tier] = (tierCounts[p.tier] || 0) + 1;
      });
      console.log('Collectibles tier distribution:', tierCounts);
    } else {
      this.productsData.collectibles = [];
      console.log('No collectibles data available - collectibles section likely not rendered');
    }

    // If the collectibles section isn't rendered on this page (e.g. NHL boxes),
    // ensure we ignore any leftover global collectibles data from previous views.
    if (!collectiblesSectionPresent) {
      if (this.productsData.collectibles.length > 0) {
        console.log('Collectibles section missing; clearing stale collectibles data for this modal context.');
      }
      this.productsData.collectibles = [];
    }

    // Set initial category based on available data
    if (this.productsData.jerseys.length > 0 && this.productsData.collectibles.length === 0) {
      this.currentCategory = 'jerseys';
    } else if (this.productsData.collectibles.length > 0 && this.productsData.jerseys.length === 0) {
      this.currentCategory = 'collectibles';
    } else if (this.productsData.jerseys.length > 0) {
      // Default to jerseys if both available
      this.currentCategory = 'jerseys';
    }

    console.log('Initial category:', this.currentCategory);
    console.log('Total products - Jerseys:', this.productsData.jerseys.length, 'Collectibles:', this.productsData.collectibles.length);
  }

  /**
   * Get products for the current category
   */
  getCategoryProducts() {
    return this.productsData[this.currentCategory] || [];
  }

  /**
   * Update category tab active states and visibility
   */
  updateCategoryTabs() {
    // Hide/show collectibles tab based on data availability
    const hasCollectibles = this.productsData.collectibles && this.productsData.collectibles.length > 0;
    const hasJerseys = this.productsData.jerseys && this.productsData.jerseys.length > 0;

    console.log('updateCategoryTabs - Jerseys:', hasJerseys, 'Collectibles:', hasCollectibles);

    // Hide the entire category tabs container if only one category has data
    const categoryTabsContainer = this.modal.querySelector('[data-modal-category-tabs]');
    if (categoryTabsContainer) {
      const visibleCategories = (hasJerseys ? 1 : 0) + (hasCollectibles ? 1 : 0);

      if (visibleCategories <= 1) {
        // Hide the entire tabs section when there's only one category
        categoryTabsContainer.setAttribute('data-hidden', 'true');
        console.log('Hiding category tabs container - only', visibleCategories, 'category available');
      } else {
        categoryTabsContainer.removeAttribute('data-hidden');
      }
    }

    // Update individual tab states
    this.categoryTabs.forEach(tab => {
      const category = tab.dataset.modalCategory;

      // Hide individual tabs if category has no data
      if (category === 'collectibles' && !hasCollectibles) {
        tab.style.display = 'none';
      } else if (category === 'jerseys' && !hasJerseys) {
        tab.style.display = 'none';
      } else {
        tab.style.display = '';
      }

      const isActive = category === this.currentCategory;
      tab.classList.toggle('active', isActive);
    });
  }

  attachEventListeners() {
    // Category tab clicks
    this.categoryTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        tab.blur();
        const newCategory = tab.dataset.modalCategory;

        // Only update if category actually changed
        if (newCategory !== this.currentCategory) {
          this.currentCategory = newCategory;
          this.currentFilter = 'all'; // Reset filter when switching categories
          this.updateCategoryTabs();
          this.updateFilterTabs();
          this.updateModalTitle();
          this.renderProducts();
        }
      });
    });

    // Filter tab clicks
    this.filterTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        tab.blur();
        this.currentFilter = tab.dataset.modalFilter;
        this.updateFilterTabs();
        this.updateModalTitle();
        this.renderProducts();
      });
    });

    // Close button
    this.closeBtn?.addEventListener('click', () => this.closeModal());

    // Backdrop click to close
    this.modal?.addEventListener('click', (e) => {
      // Check if click was on the backdrop (modal itself, not its content)
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Keyboard navigation - ESC to close
    document.addEventListener('keydown', (e) => {
      if (!this.modal.hasAttribute('open')) return;
      if (e.key === 'Escape') this.closeModal();
    });

    // Handle any modal close event (backdrop click, native close, etc.)
    this.modal.addEventListener('close', () => {
      // Always clean up body styles to prevent freezing
      const scrollPosition = parseInt(document.body.dataset.scrollPosition || 0);

      // Remove ALL fixed positioning styles that might have been applied
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';

      // Restore scroll position if we have one saved
      if (scrollPosition > 0) {
        window.requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
          document.documentElement.scrollTop = scrollPosition;
          document.body.scrollTop = scrollPosition;
        });
      }

      delete document.body.dataset.scrollPosition;
      this.isModalOpening = false;
    });

    // Click handlers for opening modal - attach to ALL mystery box containers
    const allContainers = document.querySelectorAll('[data-mystery-box-modal]');
    allContainers.forEach(container => {
      container.addEventListener('click', (e) => {
        // Get category from container data attribute
        const category = container.dataset.category || 'jerseys';

        // View all button
        const viewAllTrigger = e.target.closest('.rarity-view-all-trigger');
        if (viewAllTrigger) {
          e.preventDefault();
          e.stopPropagation();
          viewAllTrigger.blur();
          const card = viewAllTrigger.closest('[data-tier]');
          const tier = card?.dataset.tier;
          if (tier) this.openModal(tier, category);
          return;
        }

        // Product item
        const productItem = e.target.closest('.rarity-product-item');
        if (productItem) {
          e.preventDefault();
          e.stopPropagation();
          productItem.blur();
          const card = productItem.closest('[data-tier]');
          const tier = card?.dataset.tier;
          if (tier) this.openModal(tier, category);
          return;
        }

        // Entire tier section card
        const tierCard = e.target.closest('.rarity-section');
        if (tierCard && !e.target.closest('.rarity-view-all-trigger')) {
          e.preventDefault();
          e.stopPropagation();
          if (tierCard.blur) tierCard.blur();
          const tier = tierCard.dataset.tier;
          if (tier) this.openModal(tier, category);
        }
      });
    });

    console.log('Attached event listeners to', allContainers.length, 'containers');
  }

  initScrollArrows() {
    if (!this.filterScrollContainer || (!this.prevButton && !this.nextButton)) return;

    // Arrow button clicks
    this.prevButton?.addEventListener('click', () => {
      const scrollAmount = this.filterScrollContainer.clientWidth * 0.7;
      this.filterScrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    this.nextButton?.addEventListener('click', () => {
      const scrollAmount = this.filterScrollContainer.clientWidth * 0.7;
      this.filterScrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });

    // Update arrow visibility on scroll
    const updateArrowState = () => {
      const hasOverflow = this.filterScrollContainer.scrollWidth > this.filterScrollContainer.clientWidth;
      const maxScrollLeft = this.filterScrollContainer.scrollWidth - this.filterScrollContainer.clientWidth;
      const atStart = this.filterScrollContainer.scrollLeft < 1;
      const atEnd = this.filterScrollContainer.scrollLeft >= maxScrollLeft - 1;

      if (this.prevButton) {
        this.prevButton.style.opacity = (!hasOverflow || atStart) ? '0' : '1';
        this.prevButton.style.pointerEvents = (!hasOverflow || atStart) ? 'none' : 'auto';
      }

      if (this.nextButton) {
        this.nextButton.style.opacity = (!hasOverflow || atEnd) ? '0' : '1';
        this.nextButton.style.pointerEvents = (!hasOverflow || atEnd) ? 'none' : 'auto';
      }
    };

    this.filterScrollContainer.addEventListener('scroll', updateArrowState, { passive: true });
    window.addEventListener('resize', updateArrowState, { passive: true });

    // Update on modal open
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open' && this.modal.hasAttribute('open')) {
          setTimeout(updateArrowState, 100);
        }
      });
    });
    observer.observe(this.modal, { attributes: true });

    updateArrowState();
  }

  filterProducts(products, filter) {
    if (filter === 'all') return products;
    return products.filter(p => p.tier === filter);
  }

  updateFilterTabs() {
    this.filterTabs.forEach(tab => {
      const filter = tab.dataset.modalFilter;
      const isActive = filter === this.currentFilter;
      tab.classList.toggle('active', isActive);
    });
  }

  updateModalTitle() {
    // Include category name in title
    const categoryName = this.currentCategory === 'jerseys' ? 'Jerseys' : 'Collectibles';

    if (this.currentFilter === 'all') {
      this.modalTitle.textContent = `All ${categoryName}`;
    } else {
      // Get tier label from config
      const tierNames = this.config?.tierNames || {
        legendary: 'Grade A',
        rare: 'Grade B',
        common: 'Grade C'
      };
      const emojis = {
        legendary: '⭐',
        rare: '🎯',
        common: '📦'
      };
      const tierLabel = `${emojis[this.currentFilter] || ''} ${tierNames[this.currentFilter] || this.currentFilter}`;
      this.modalTitle.textContent = `${tierLabel} - ${categoryName}`;
    }
  }

  renderProducts() {
    // Get products from current category
    const categoryProducts = this.getCategoryProducts();
    const productsToRender = this.filterProducts(categoryProducts, this.currentFilter);

    if (this.config.showCounts) {
      this.modalCount.textContent = `${productsToRender.length} items`;
    }

    // Render product cards
    this.modalGrid.innerHTML = productsToRender.map((product, index) => {
      const srcset = [
        product.image400 ? `${product.image400} 400w` : '',
        product.image600 ? `${product.image600} 600w` : '',
        product.image800 ? `${product.image800} 800w` : ''
      ].filter(Boolean).join(', ');

      const sizes = '(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 250px';
      const authBadge = getAuthBadgeHTML(product.authenticatedBy);

      return `
        <a href="${product.url}" class="mystery-box-product-card" target="_blank" rel="noopener">
          <img
            src="${product.image600 || product.image400 || product.image || ''}"
            ${srcset ? `srcset="${srcset}"` : ''}
            sizes="${sizes}"
            alt="${product.product_name}"
            class="mystery-box-product-card__image"
            width="${product.width || 800}"
            height="${product.height || 800}"
            loading="${index < 8 ? 'eager' : 'lazy'}"
            decoding="${index < 4 ? 'sync' : 'async'}"
          />
          <div class="mystery-box-product-card__details">
            <div class="mystery-box-product-card__name">${product.product_name}</div>
            ${authBadge ? `<div class="mystery-box-product-card__auth">${authBadge}</div>` : ''}
            <div class="mystery-box-product-card__price-wrapper">
              <span class="mystery-box-product-card__price-label">MSRP</span>
              <span class="mystery-box-product-card__price-value">${product.compareAtPriceFormatted || product.priceFormatted}</span>
            </div>
          </div>
        </a>
      `;
    }).join('');

    applyAuthLogoSizing(this.modalGrid);

    // Fade-in effect for loaded images
    requestAnimationFrame(() => {
      const images = this.modalGrid.querySelectorAll('img');
      images.forEach(img => {
        if (img.complete) {
          img.classList.add('loaded');
        } else {
          img.addEventListener('load', () => {
            img.classList.add('loaded');
          }, { once: true });
        }
      });
    });
  }

  openModal(tier, category = null) {
    if (this.isModalOpening) return;
    this.isModalOpening = true;

    // Set category if provided, otherwise keep current
    if (category) {
      // Validate the category has data
      const hasCategory = this.productsData[category] && this.productsData[category].length > 0;
      if (hasCategory) {
        this.currentCategory = category;
      } else {
        // Default to jerseys if requested category has no data
        if (this.productsData.jerseys && this.productsData.jerseys.length > 0) {
          this.currentCategory = 'jerseys';
        } else if (this.productsData.collectibles && this.productsData.collectibles.length > 0) {
          this.currentCategory = 'collectibles';
        }
      }
    }

    this.currentFilter = tier;
    this.updateCategoryTabs();
    this.updateFilterTabs();
    this.updateModalTitle();
    this.renderProducts();

    // Let base modal.js handle scroll locking - it already does this correctly
    this.modal.open();

    // Reset flag after modal opens
    setTimeout(() => {
      this.isModalOpening = false;
    }, 100);
  }

  closeModal() {
    // Let base modal.js handle scroll restoration - it already does this correctly
    this.modal.close();

    setTimeout(() => {
      this.isModalOpening = false;
    }, 200);
  }
}

// Export for use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    MysteryBoxRarity,
    VerticalSections,
    MysteryBoxModalController
  };
}
